import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dexName } from "../lib/dex";
import { formatPercent } from "../lib/format";
import type { Pool } from "../types";

type Detail = "fee" | "warning";
const EDGE = 8;

function warnings(pool: Pool) {
  return [
    pool.warning,
    ...[pool.tokenA, pool.tokenB]
      .filter((token) => token.symbolMissing)
      .map(
        (token) =>
          `${dexName(pool.dex)} did not provide a ticker symbol for mint ${token.mint}. The shortened mint shown here is a display fallback.`,
      ),
  ].filter((value): value is string => Boolean(value));
}

export function PoolDetails({ pool }: { pool: Pool }) {
  const [open, setOpen] = useState<Detail | null>(null);
  const [position, setPosition] = useState({ left: EDGE, top: EDGE });
  const feeButton = useRef<HTMLButtonElement>(null);
  const warningButton = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialogId = useId();
  const titleId = useId();
  const poolWarnings = warnings(pool);

  const trigger = open === "warning" ? warningButton : feeButton;
  const close = useCallback(
    (restoreFocus: boolean) => {
      setOpen(null);
      if (restoreFocus) requestAnimationFrame(() => trigger.current?.focus());
    },
    [trigger],
  );
  const place = useCallback(() => {
    const anchor = trigger.current;
    const panel = popup.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    if (
      (a.width > 0 || a.height > 0) &&
      (a.bottom <= 0 || a.top >= innerHeight || a.right <= 0 || a.left >= innerWidth)
    ) {
      setOpen(null);
      return;
    }
    const p = panel.getBoundingClientRect();
    const left = Math.min(Math.max(EDGE, a.left), Math.max(EDGE, innerWidth - p.width - EDGE));
    const below = a.bottom + 6;
    const top =
      below + p.height <= innerHeight - EDGE ? below : Math.max(EDGE, a.top - p.height - 6);
    setPosition({ left, top });
  }, [trigger]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    closeButton.current?.focus({ preventScroll: true });
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popup.current?.contains(target) && !trigger.current?.contains(target)) close(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    addEventListener("pointerdown", onPointerDown);
    addEventListener("keydown", onKeyDown);
    addEventListener("resize", place);
    addEventListener("scroll", place, true);
    return () => {
      removeEventListener("pointerdown", onPointerDown);
      removeEventListener("keydown", onKeyDown);
      removeEventListener("resize", place);
      removeEventListener("scroll", place, true);
    };
  }, [close, open, place, trigger]);

  const details = pool.feeDetails;
  const mode = details?.mode ?? "unknown";
  const feeLabel = mode === "adaptive" ? "base" : mode === "unknown" ? "reported" : "fee";
  const baseFeePercent = formatPercent(pool.feePercent);
  return (
    <div className="pool-detail-controls">
      <button
        ref={feeButton}
        className="fee-badge"
        aria-label={`${baseFeePercent} ${feeLabel}`}
        aria-expanded={open === "fee"}
        aria-controls={dialogId}
        title={`${baseFeePercent} ${feeLabel}`}
        onClick={() => {
          setOpen((value) => (value === "fee" ? null : "fee"));
        }}
      >
        {baseFeePercent}
      </button>
      {poolWarnings.length > 0 && (
        <button
          ref={warningButton}
          className="pool-warning-button"
          aria-label="Pool metadata warning"
          aria-expanded={open === "warning"}
          aria-controls={dialogId}
          onClick={() => {
            setOpen((value) => (value === "warning" ? null : "warning"));
          }}
        >
          !
        </button>
      )}
      {open &&
        createPortal(
          <div
            ref={popup}
            id={dialogId}
            className="pool-details-popup"
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            style={position}
          >
            <div className="pool-details-heading">
              <strong id={titleId}>
                {open === "fee" ? "Trading fee details" : "Pool warning"}
              </strong>
              <button
                ref={closeButton}
                onClick={() => {
                  close(true);
                }}
              >
                Close
              </button>
            </div>
            {open === "fee" ? (
              <FeeContent pool={pool} />
            ) : (
              poolWarnings.map((warning) => <p key={warning}>{warning}</p>)
            )}
            <small>Pool address</small>
            <code className="pool-full-address">{pool.address}</code>
          </div>,
          document.body,
        )}
    </div>
  );
}

function FeeContent({ pool }: { pool: Pool }) {
  const details = pool.feeDetails;
  const baseFeePercent = formatPercent(pool.feePercent);
  if (!details || details.mode === "unknown")
    return (
      <p>
        {baseFeePercent} is the reported fee. The fee model and current effective rate are
        unavailable.
      </p>
    );
  if (details.mode === "static")
    return (
      <>
        <p>This pool uses a fixed trading fee.</p>
        <dl>
          <dt>Current trading fee</dt>
          <dd>{formatPercent(details.currentFeePercent ?? pool.feePercent)}</dd>
        </dl>
      </>
    );
  return (
    <>
      <p>
        The displayed fee is the base component. The adaptive surcharge can change; these values are
        a catalog snapshot, not an execution quote.
      </p>
      <dl>
        <dt>Base fee</dt>
        <dd>{baseFeePercent}</dd>
        <dt>Adaptive surcharge</dt>
        <dd>{formatPercent(details.adaptiveFeePercent)}</dd>
        <dt>Current trading fee</dt>
        <dd>{formatPercent(details.currentFeePercent)}</dd>
      </dl>
    </>
  );
}
